/**
 * Trade-entry ticket taxonomy — the single source of truth for where each
 * ticket lives in the sidebar and where its booked instrument lands in the
 * governed security classification (ref_security_classification_node).
 *
 * The rule (docs/product/security-classification-hierarchy-FRD.md §5, and the
 * trade-entry alignment proposal): entry groups may be FINER than the
 * taxonomy — Derivatives by contract type is good workflow — but must never
 * CONTRADICT it. Placement follows the instrument held; strategy, sponsor,
 * and distress are facets, not menu groups. Booking a leveraged loan from a
 * "Private Credit" menu group while the register shows it under Public Credit
 * is the drift this module exists to prevent.
 *
 * Sleeve groups carry the canonical L1 node code; `assertMenuMatchesTaxonomy`
 * throws in development when an item sits in a sleeve group whose L1 differs
 * from the item's own — so the contradiction class fails the build, not the
 * user's trust.
 */

/** Canonical L1 sleeves, mirroring ref_security_classification_node. */
export const CANONICAL_SLEEVES: Record<string, string> = {
  PUBLIC_CREDIT: 'Public Credit',
  PRIVATE_CREDIT: 'Private Credit',
  STRUCTURED_CREDIT: 'Structured Credit',
  PUBLIC_EQUITY: 'Public Equity',
  REAL_ASSETS: 'Real Assets',
  ALTERNATIVES: 'Alternatives',
  DERIVATIVES: 'Derivatives',
  FINANCING: 'Financing',
  CASH_EQUIVALENTS: 'Cash & Equivalents',
};

export type TicketTaxonomyEntry = {
  /** Route under /trade-entry — never changed by menu realignments. */
  path: string;
  /** Sidebar label. */
  label: string;
  icon: string;
  /** Canonical L1 node code the booked instrument lands in. */
  l1: keyof typeof CANONICAL_SLEEVES;
  /** Canonical L2 node code where the ticket maps 1:1; null for multi-L2 or workflow tickets. */
  l2: string | null;
};

export type TicketMenuGroup = {
  /** Sidebar group label. For sleeve groups this must equal the L1 display name. */
  label: string;
  icon: string;
  /**
   * L1 sleeve this group asserts, or null for workflow families (Derivatives,
   * FX, …) that are finer than the taxonomy by design. Workflow items still
   * declare their true l1 on each entry.
   */
  sleeve: keyof typeof CANONICAL_SLEEVES | null;
  items: TicketTaxonomyEntry[];
};

export const TRADE_ENTRY_MENU: TicketMenuGroup[] = [
  {
    label: 'Public Credit',
    icon: '📊',
    sleeve: 'PUBLIC_CREDIT',
    items: [
      // Corp / Govt / Muni bond ticket — multi-L2 within the sleeve.
      { path: 'fixed-income', label: 'Bonds', icon: '📊', l1: 'PUBLIC_CREDIT', l2: null },
      // Syndicated, traded loan — Public Credit by the instrument-held rule.
      // Sat under "Private Credit" until the 2026-08 alignment, contradicting
      // the register (28 live securities under PUBLIC_CREDIT → LEV_LOAN).
      { path: 'leveraged-loan', label: 'Leveraged Loan', icon: '🏦', l1: 'PUBLIC_CREDIT', l2: 'LEV_LOAN' },
    ],
  },
  {
    label: 'Public Equity',
    icon: '📈',
    sleeve: 'PUBLIC_EQUITY',
    items: [
      { path: 'equity', label: 'Equity', icon: '📈', l1: 'PUBLIC_EQUITY', l2: 'PUBLIC_EQUITY_SHARE' },
    ],
  },
  {
    // Originated / directly-held credit.
    label: 'Private Credit',
    icon: '🏦',
    sleeve: 'PRIVATE_CREDIT',
    items: [
      { path: 'private-fixed-income', label: 'Direct Loan', icon: '🏦', l1: 'PRIVATE_CREDIT', l2: 'DIRECT_LOAN' },
      // A loan to a portfolio company is a loan; the sponsor / PE relationship
      // is a facet. Moved from the retired Private Equity group.
      { path: 'portco-debt', label: 'Portco Debt', icon: '🏛', l1: 'PRIVATE_CREDIT', l2: 'DIRECT_LOAN' },
      { path: 'abf-facility', label: 'ABF Facility', icon: '▦', l1: 'PRIVATE_CREDIT', l2: 'ABF_FACILITY' },
    ],
  },
  {
    // Securitized / traded structured credit. MSR is NOT here — the FRD places
    // servicing rights under Alternatives (§5.2).
    label: 'Structured Credit',
    icon: '🧱',
    sleeve: 'STRUCTURED_CREDIT',
    items: [
      { path: 'rmbs-tranche', label: 'RMBS Tranche', icon: '🏠', l1: 'STRUCTURED_CREDIT', l2: 'RMBS_TRANCHE' },
      { path: 'abs-tranche', label: 'ABS Tranche', icon: '▤', l1: 'STRUCTURED_CREDIT', l2: 'ABS_TRANCHE' },
      { path: 'cmbs-tranche', label: 'CMBS Tranche', icon: '🏢', l1: 'STRUCTURED_CREDIT', l2: 'CMBS_TRANCHE' },
      { path: 'clo-equity', label: 'CLO Tranche', icon: '🏛', l1: 'STRUCTURED_CREDIT', l2: 'CLO_TRANCHE' },
      // Whole-loan pools are not securitized, but the governed tree places
      // RESI_WHOLE_LOAN under Structured Credit — the tree is the authority.
      // (The alignment proposal originally sketched this under Real Assets;
      // corrected against the live taxonomy.)
      { path: 'rml', label: 'Residential Whole Loan', icon: '🏘', l1: 'STRUCTURED_CREDIT', l2: 'RESI_WHOLE_LOAN' },
    ],
  },
  {
    label: 'Real Assets',
    icon: '🏢',
    sleeve: 'REAL_ASSETS',
    items: [
      { path: 'real-estate', label: 'Property', icon: '🏛', l1: 'REAL_ASSETS', l2: 'PROPERTY' },
      { path: 'real-estate-cml', label: 'CRE Loan', icon: '🏗', l1: 'REAL_ASSETS', l2: 'CRE_LOAN' },
    ],
  },
  {
    // Fund-form and non-security holdings (FRD §5.2). PE Commitment becomes
    // the generalized Fund Commitment ticket (strategy facet) in Phase B.
    label: 'Alternatives',
    icon: '🎯',
    sleeve: 'ALTERNATIVES',
    items: [
      { path: 'private-equity', label: 'PE Commitment', icon: '🎯', l1: 'ALTERNATIVES', l2: 'FUND_INTEREST' },
      { path: 'msr', label: 'MSR', icon: '🧾', l1: 'ALTERNATIVES', l2: 'MSR' },
    ],
  },
  {
    // Workflow family — finer than L2 (contract type is L3). Allowed.
    label: 'Derivatives',
    icon: '📉',
    sleeve: null,
    items: [
      { path: 'derivatives/ir-swap', label: 'IR Swap', icon: '🔄', l1: 'DERIVATIVES', l2: 'DERIV_SWAP' },
      { path: 'derivatives/tr-swap', label: 'Total Return Swap', icon: '📊', l1: 'DERIVATIVES', l2: 'DERIV_SWAP' },
      { path: 'derivatives/currency-swap', label: 'Currency Swap', icon: '💱', l1: 'DERIVATIVES', l2: 'DERIV_SWAP' },
      { path: 'derivatives/cd-swap', label: 'CD Swap', icon: '💳', l1: 'DERIVATIVES', l2: 'DERIV_SWAP' },
      { path: 'derivatives/swaption', label: 'Swaption', icon: '🎯', l1: 'DERIVATIVES', l2: 'DERIV_OPTION' },
      { path: 'derivatives/futures', label: 'Futures', icon: '📅', l1: 'DERIVATIVES', l2: 'DERIV_FUTURE' },
      { path: 'derivatives/options', label: 'Options', icon: '📊', l1: 'DERIVATIVES', l2: 'DERIV_OPTION' },
    ],
  },
  {
    // Workflow family — maps to Derivatives → FX at L2, own group for flow.
    label: 'FX',
    icon: '💱',
    sleeve: null,
    items: [
      { path: 'fx/spot', label: 'Spot', icon: '💱', l1: 'DERIVATIVES', l2: 'DERIV_FX' },
      { path: 'fx/forward', label: 'Forward', icon: '📅', l1: 'DERIVATIVES', l2: 'DERIV_FX' },
      { path: 'fx/swap', label: 'FX Swap', icon: '🔄', l1: 'DERIVATIVES', l2: 'DERIV_FX' },
      { path: 'fx/ndf', label: 'NDF', icon: '📊', l1: 'DERIVATIVES', l2: 'DERIV_FX' },
    ],
  },
  {
    label: 'Repo',
    icon: '🔁',
    sleeve: null,
    items: [{ path: 'repo', label: 'Repo', icon: '🔁', l1: 'FINANCING', l2: 'REPO' }],
  },
  {
    label: 'Cash',
    icon: '💵',
    sleeve: null,
    items: [{ path: 'cash', label: 'Cash', icon: '💵', l1: 'CASH_EQUIVALENTS', l2: 'CASH' }],
  },
  {
    // Workflow tool spanning asset classes — outside the taxonomy on purpose.
    label: 'Multi-Leg',
    icon: '🔗',
    sleeve: null,
    items: [{ path: 'multi-leg', label: 'Multi-Leg', icon: '🔗', l1: 'DERIVATIVES', l2: null }],
  },
];

/**
 * Items allowed to sit in a sleeve group that is not their own L1, each with
 * the reason on record. Keep this list SHORT — every entry is a debt.
 * Currently empty; it exists so a future exception is a recorded decision,
 * not a silent contradiction.
 */
const PLACEMENT_EXEMPTIONS: Record<string, string> = {};

/**
 * Dev-time lock: a ticket in a sleeve group must land in that sleeve, unless
 * it carries a recorded exemption. Throws so the contradiction class fails in
 * development instead of shipping.
 */
export function assertMenuMatchesTaxonomy(menu: TicketMenuGroup[] = TRADE_ENTRY_MENU): void {
  const errors: string[] = [];
  for (const group of menu) {
    if (group.sleeve) {
      if (CANONICAL_SLEEVES[group.sleeve] !== group.label) {
        errors.push(`Group "${group.label}" asserts sleeve ${group.sleeve} but the canonical display name is "${CANONICAL_SLEEVES[group.sleeve]}".`);
      }
      for (const item of group.items) {
        if (item.l1 !== group.sleeve && !PLACEMENT_EXEMPTIONS[item.path]) {
          errors.push(`Ticket "${item.label}" (${item.path}) lands in ${item.l1} but sits in the "${group.label}" group.`);
        }
      }
    }
  }
  if (errors.length > 0) {
    throw new Error(`Trade-entry menu contradicts the classification taxonomy:\n- ${errors.join('\n- ')}`);
  }
}

if (process.env.NODE_ENV !== 'production') {
  assertMenuMatchesTaxonomy();
}
